// Payouts — the Superadmin Send table (Leon 2026-10-09). Nothing leaves the
// PayMongo wallet until a Superadmin presses Send here, and every send is in
// the audit log. Only a Superadmin reaches this page (permissions.ts) and the
// payouts function re-checks the role.

import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { PageHeader } from "@bednbody/ui/components/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bednbody/ui/components/alert-dialog";
import { Badge } from "@bednbody/ui/components/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { EdgeFunctionError } from "@/lib/edge-functions";
import {
  type AdminPayout,
  type PayoutStage,
  STAGE_LABEL,
  usePayoutAccount,
  usePayouts,
  useRefreshPayout,
  useSendPayout,
} from "./payouts.hooks";

const peso = new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" });
const when = (iso: string) =>
  new Date(iso).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });

const STAGE_TONE: Record<
  PayoutStage,
  "warning-soft" | "info-soft" | "success-soft" | "destructive-soft" | "muted-soft" | "primary-soft"
> = {
  pending: "muted-soft",
  awaiting_approval: "primary-soft",
  sending: "info-soft",
  paid: "success-soft",
  failed: "destructive-soft",
  returned: "destructive-soft",
  cancelled: "muted-soft",
};

function messageOf(e: unknown): string {
  return e instanceof EdgeFunctionError ? e.message : "Something went wrong. Try again.";
}

function AccountDialog({ payout, onClose }: { payout: AdminPayout | null; onClose: () => void }) {
  const account = usePayoutAccount(payout?.id ?? null);
  return (
    <Dialog
      open={payout !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Payout account</DialogTitle>
          <DialogDescription>
            Decrypted for you only. Viewing it is not logged as a send.
          </DialogDescription>
        </DialogHeader>
        {account.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : account.error ? (
          <p className="text-sm text-destructive">{messageOf(account.error)}</p>
        ) : account.data ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Bank or e-wallet</dt>
            <dd>{account.data.bankName}</dd>
            <dt className="text-muted-foreground">Account name</dt>
            <dd>{account.data.accountName}</dd>
            <dt className="text-muted-foreground">Account number</dt>
            <dd className="tabular-nums">{account.data.accountNumber}</dd>
          </dl>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function PayoutsPage() {
  const { data, isLoading, error } = usePayouts();
  const send = useSendPayout();
  const refresh = useRefreshPayout();
  const [confirming, setConfirming] = useState<AdminPayout | null>(null);
  const [viewing, setViewing] = useState<AdminPayout | null>(null);
  const [notice, setNotice] = useState<{ tone: "error" | "ok"; text: string } | null>(null);

  const doSend = (payout: AdminPayout) => {
    setNotice(null);
    send.mutate(payout.id, {
      onSuccess: () =>
        setNotice({
          tone: "ok",
          text: `Sent ${peso.format(payout.amountPhp)} to ${payout.staffName}.`,
        }),
      onError: (e) => setNotice({ tone: "error", text: messageOf(e) }),
      onSettled: () => setConfirming(null),
    });
  };

  const columns: ColumnDef<AdminPayout, unknown>[] = [
    {
      id: "staff",
      accessorFn: (p) => p.staffName,
      header: "Staff",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.staffName}
          subtitle={row.original.staffRole}
          fallback={row.original.staffName.slice(0, 2).toUpperCase()}
        />
      ),
    },
    {
      id: "amount",
      accessorFn: (p) => p.amountPhp,
      header: "Amount",
      cell: ({ row }) => (
        <span className="tabular-nums">{peso.format(row.original.amountPhp)}</span>
      ),
    },
    {
      id: "account",
      accessorFn: (p) => `${p.bankName ?? ""} ${p.last4 ?? ""}`,
      header: "To",
      cell: ({ row }) =>
        row.original.bankName ? `${row.original.bankName} ••${row.original.last4 ?? ""}` : "—",
    },
    {
      id: "stage",
      accessorFn: (p) => p.stage,
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={STAGE_TONE[row.original.stage]}>{STAGE_LABEL[row.original.stage]}</Badge>
      ),
    },
    {
      id: "created",
      accessorFn: (p) => p.createdAt,
      header: "Requested",
      cell: ({ row }) => when(row.original.createdAt),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => {
        const p = row.original;
        return (
          <AdminTableRowMenu
            label={p.staffName}
            actions={[
              ...(p.stage === "awaiting_approval"
                ? [{ label: "Send", onSelect: () => setConfirming(p) }]
                : []),
              ...(p.bankName ? [{ label: "View account", onSelect: () => setViewing(p) }] : []),
              ...(p.stage === "sending" && p.providerRef
                ? [{ label: "Check status", onSelect: () => refresh.mutate(p.id) }]
                : []),
            ]}
          />
        );
      },
    },
  ];

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        description="Staff cash-out requests. Nothing is sent until you press Send."
        title="Payouts"
      />

      {notice ? (
        <p
          role={notice.tone === "error" ? "alert" : "status"}
          className={notice.tone === "error" ? "text-sm text-destructive" : "text-sm text-success"}
        >
          {notice.text}
        </p>
      ) : null}

      {error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : (
        <AdminDataTable
          presentation="standalone"
          data={data ?? []}
          columns={columns}
          loading={isLoading}
          getRowId={(p) => p.id}
          searchText={(p) =>
            [p.staffName, p.bankName ?? "", p.last4 ?? "", p.providerRef ?? ""].join(" ")
          }
          searchPlaceholder="Search name, bank, last 4…"
          facets={[
            {
              id: "stage",
              label: "Status",
              options: (Object.keys(STAGE_LABEL) as PayoutStage[]).map((s) => ({
                value: s,
                label: STAGE_LABEL[s],
              })),
              getValue: (p) => p.stage,
            },
          ]}
          initialFacets={{ stage: "awaiting_approval" }}
          emptyTitle="No payout requests"
          emptyDescription="Requests appear here once staff confirm their code."
        />
      )}

      <AlertDialog
        open={confirming !== null}
        onOpenChange={(next) => {
          if (!next && !send.isPending) setConfirming(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Send {confirming ? peso.format(confirming.amountPhp) : ""}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              To {confirming?.staffName}, {confirming?.bankName} ••{confirming?.last4}. Money leaves
              the PayMongo wallet straight away and this can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={send.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={send.isPending}
              onClick={() => {
                if (confirming) doSend(confirming);
              }}
            >
              {send.isPending ? "Sending…" : "Send"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AccountDialog payout={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
